import React from 'react';
import styled from 'styled-components';
import useSWR from 'swr';
import { COUNTRY_CODES, CountryCode, Place, LocationPlaces } from '../shared';
import { useDebounce } from 'use-debounce';
import { ReactComponent as MarkerSvg } from 'assets/icons/marker.svg';
import { Flex } from 'components/Flex';
import { AutoSuggest, AutoSuggestItem } from 'components/AutoSuggest';
import { Label } from './components/Label';

type Props = {
  location?: LocationPlaces;
  onLocationChange?: (location: LocationPlaces) => void;
};

const Location = (props: Props) => {
  const { location, onLocationChange = () => {} } = props;
  const { from, to } = location || {};

  const defaultFromSearch = from
    ? [from.placeName, from.postalCode].join(', ')
    : undefined;

  const defaultToSearch = to
    ? [to.placeName, to.postalCode].join(', ')
    : undefined;

  return (
    <Flex direction="row" spacing={['small', 'small']}>
      <Label text="From">
        <Selector
          name="from"
          defaultSearch={defaultFromSearch}
          country={getOppositeCountryCode(location?.to?.countryCode)}
          onLocationSelect={from => {
            onLocationChange({ ...location, from: from ?? undefined });
          }}
        />
      </Label>
      <Label text="To">
        <Selector
          name="to"
          defaultSearch={defaultToSearch}
          country={getOppositeCountryCode(location?.from?.countryCode)}
          onLocationSelect={to => {
            onLocationChange({ ...location, to: to ?? undefined });
          }}
        />
      </Label>
    </Flex>
  );
};

type Data = {
  postalCodes: Place[];
};

type SelectorProps = {
  name: string;
  defaultSearch?: string;
  country?: Place['countryCode'];
  onLocationSelect?: (place: Place | null) => void;
};

const SEARCH_POSTCODES = '/api/postcode-search';

const Selector = (props: SelectorProps) => {
  const {
    defaultSearch = '',
    country = COUNTRY_CODES,
    onLocationSelect = () => {},
  } = props;
  const hasSelectionRef = React.useRef(false);
  const [search, setSearch] = React.useState(defaultSearch);
  const [debouncedSearch] = useDebounce(search, 200);
  const location = React.useMemo(() => {
    const [city, zip] = debouncedSearch.split(/,\s?/);
    return country && city ? { country, city, zip } : null;
  }, [country, debouncedSearch]);

  const { data } = useSWR<Data>(location ? [SEARCH_POSTCODES, location] : null);
  const isLoading = !data;

  function getLocationFromLabel(label: string) {
    return data?.postalCodes.find(place => getPlaceLabel(place) === label);
  }

  function handleSearchChange(search: string) {
    setSearch(search);
    onLocationSelect(null);
    hasSelectionRef.current = false;
  }

  function handleItemSelect(search: string) {
    const location = getLocationFromLabel(search);
    setSearch(search);

    if (location) {
      hasSelectionRef.current = true;
      onLocationSelect(location);
    }
  }

  function handleBlur() {
    if (!hasSelectionRef.current) {
      setSearch('');
    }
  }

  return (
    <Wrapper>
      <StyledAutoSuggest
        search={search}
        onSearchChange={handleSearchChange}
        onItemSelect={handleItemSelect}
        onBlur={handleBlur}
      >
        {isLoading ? (
          <AutoSuggestItem value="">Loading&hellip;</AutoSuggestItem>
        ) : (
          data?.postalCodes.map(place => {
            const label = getPlaceLabel(place);
            return <AutoSuggestItem key={label}>{label}</AutoSuggestItem>;
          })
        )}
      </StyledAutoSuggest>
      <MarkerIcon role="presentation" />
    </Wrapper>
  );
};

function getOppositeCountryCode(countryCode?: CountryCode) {
  return countryCode ? (countryCode === 'IT' ? 'SI' : 'IT') : undefined;
}

function getPlaceLabel(place: Place) {
  return `${place.placeName}, ${place.postalCode}`;
}

const Wrapper = styled.div`
  position: relative;
`;

const MarkerIcon = styled(MarkerSvg)`
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
`;

const StyledAutoSuggest = styled(AutoSuggest)`
  padding-left: 45px;
  width: 100%;
`;

export { Location };
